import { useCallback, useRef, useState } from "react";
import { AppBar } from "../components/common/AppBar";
import { ChatPanel } from "../components/ChatPanel/ChatPanel";
import { FieldsPanel } from "../components/FieldsPanel/FieldsPanel";
import { DocPreview } from "../components/DocPreview/DocPreview";
import { SubmitModal } from "../components/SubmitModal";
import { DocStoreProvider, useDocStore } from "../state/docStore";
import "./Workspace.css";

type LeftTab = "chat" | "fields";

function WorkspaceInner() {
  const { doc } = useDocStore();
  const [tab, setTab] = useState<LeftTab>("chat");
  const [showSubmit, setShowSubmit] = useState(false);

  // Resizable split: left pane width as a percentage of the container.
  const [leftPct, setLeftPct] = useState(46);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);
  const [dragging, setDragging] = useState(false);

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!draggingRef.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    setLeftPct(Math.min(70, Math.max(30, pct)));
  }, []);

  const stopDrag = useCallback(() => {
    draggingRef.current = false;
    setDragging(false);
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", stopDrag);
  }, [onMouseMove]);

  const startDrag = useCallback(() => {
    draggingRef.current = true;
    setDragging(true);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", stopDrag);
  }, [onMouseMove, stopDrag]);

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(doc.contactEmail.trim());
  const submitBlockers = [
    doc.companyName.trim() ? null : "company name",
    doc.participantName.trim() ? null : "your name",
    emailOk ? null : "a valid contact email",
    doc.mainGoal.trim() ? null : "a main goal",
  ].filter((x): x is string => x !== null);
  const canSubmit = submitBlockers.length === 0;

  return (
    <div className="workspace">
      <AppBar />
      <div className="workspace__body" ref={containerRef}>
        <div className="workspace__left" style={{ width: `${leftPct}%` }}>
          <div className="workspace__tabs" role="tablist">
            <button
              role="tab"
              aria-selected={tab === "chat"}
              className={`workspace__tab ${tab === "chat" ? "workspace__tab--active" : ""}`}
              onClick={() => setTab("chat")}
            >
              Chat
            </button>
            <button
              role="tab"
              aria-selected={tab === "fields"}
              className={`workspace__tab ${tab === "fields" ? "workspace__tab--active" : ""}`}
              onClick={() => setTab("fields")}
            >
              Edit fields
            </button>
          </div>
          <div className="workspace__tab-body">
            {tab === "chat" ? <ChatPanel /> : <FieldsPanel />}
          </div>
        </div>

        <div
          className={`workspace__divider ${dragging ? "workspace__divider--active" : ""}`}
          onMouseDown={startDrag}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panes"
        />

        <div className="workspace__right">
          <DocPreview
            canSubmit={canSubmit}
            submitBlockers={submitBlockers}
            onSubmit={() => setShowSubmit(true)}
          />
        </div>
      </div>

      {showSubmit && <SubmitModal onClose={() => setShowSubmit(false)} />}
    </div>
  );
}

export function Workspace() {
  return (
    <DocStoreProvider>
      <WorkspaceInner />
    </DocStoreProvider>
  );
}
